import type * as monacoType from "monaco-editor";

import {
    buildPlatformThemeTokens,
    ensureContrast,
    ensureContrastAcross,
    PlatformThemeRecord,
} from "app/theme/platformTheme";

const MONACO_PLATFORM_THEME_NAME = "primum-platform-editor";

export const buildMonacoThemeData = (themeRecord: PlatformThemeRecord): monacoType.editor.IStandaloneThemeData => {
    const { colors, mode } = buildPlatformThemeTokens(themeRecord);
    const background = colors.surfaceRaised;
    const foreground = (color: string) => ensureContrastAcross(color, [background, colors.surface]).slice(1);
    const highlightBorder = ensureContrast(colors.border, background, 3);
    const bracketColor = (color: string) => `#${foreground(color)}`.toUpperCase();
    const bracketPalette = mode === "dark"
        ? ["#FFD700", "#DA70D6", "#179FFF", "#FFD700", "#DA70D6", "#179FFF"]
        : ["#0431FA", "#319331", "#7B3814", "#0431FA", "#319331", "#7B3814"];

    return {
        base: mode === "dark" ? "vs-dark" : "vs",
        inherit: true,
        rules: [
            { token: "keyword", foreground: foreground(mode === "dark" ? "#569CD6" : "#0000FF") },
            { token: "string", foreground: foreground(mode === "dark" ? "#CE9178" : "#A31515") },
            { token: "string.key.json", foreground: foreground(mode === "dark" ? "#9CDCFE" : "#A31515") },
            { token: "string.value.json", foreground: foreground(mode === "dark" ? "#CE9178" : "#0451A5") },
            { token: "keyword.json", foreground: foreground(mode === "dark" ? "#CE9178" : "#0451A5") },
            { token: "number", foreground: foreground(mode === "dark" ? "#B5CEA8" : "#098658") },
            { token: "delimiter", foreground: foreground(colors.textSecondary) },
            { token: "delimiter.bracket", foreground: foreground(mode === "dark" ? "#DCDCAA" : "#319331") },
            { token: "comment", foreground: foreground(mode === "dark" ? "#6A9955" : "#008000") },
        ],
        colors: {
            "editor.background": background,
            "editor.foreground": colors.textPrimary,
            "editorLineNumber.foreground": colors.textDisabled,
            "editorLineNumber.activeForeground": colors.textPrimary,
            "editorCursor.foreground": colors.textPrimary,// colors.primary.main,
            "editor.selectionBackground": colors.textDisabled,//colors.actionSelected,
            "editor.inactiveSelectionBackground": colors.textDisabled,//colors.actionHover,
            "editor.lineHighlightBackground": colors.surface,//colors.actionHover,
            "editor.lineHighlightBorder": highlightBorder,
            ...Object.fromEntries(bracketPalette.map((color, index) => [`editorBracketHighlight.foreground${index + 1}`, bracketColor(color)])),
            "editorBracketHighlight.unexpectedBracket.foreground": bracketColor(colors.error?.main ?? "#DC2626"),
            "editorBracketMatch.border": highlightBorder,
            "editorBracketMatch.background": colors.surface,
            "editorIndentGuide.background1": colors.border,
            "editorIndentGuide.activeBackground1": colors.textSecondary,
            "editorWhitespace.foreground": colors.border,
            "editorWidget.background": colors.surface,
            "editorWidget.border": colors.border,
            "input.background": colors.surface,
            "input.foreground": colors.textPrimary,
            "input.border": colors.border,
            "dropdown.background": colors.surface,
            "dropdown.foreground": colors.textPrimary,
            "dropdown.border": colors.border,
            "list.activeSelectionBackground": colors.textDisabled,//colors.actionSelected,
            "list.activeSelectionForeground": colors.textPrimary,
            "list.hoverBackground": colors.textSecondary,//colors.actionHover,
            "list.hoverForeground": colors.textPrimary,
            "list.focusBackground": colors.actionSelected,
            "list.focusForeground": colors.textPrimary,
            "list.inactiveSelectionBackground": colors.textSecondary,//colors.actionHover,
            "list.inactiveSelectionForeground": colors.textPrimary,
            "scrollbarSlider.background": colors.scrollbarThumb,
            "scrollbarSlider.hoverBackground": colors.scrollbarThumbHover,
            "scrollbarSlider.activeBackground": colors.primary.main,
            "minimap.background": colors.surfaceRaised,
            "minimap.selectionHighlight": colors.actionSelected,
            "minimapSlider.background": colors.scrollbarThumb,
            "minimapSlider.hoverBackground": colors.scrollbarThumbHover,
            "minimapSlider.activeBackground": colors.primary.main,
        },
    };
};

export const applyMonacoPlatformTheme = (
    monaco: typeof monacoType,
    themeRecord: PlatformThemeRecord
) => {
    monaco.editor.defineTheme(MONACO_PLATFORM_THEME_NAME, buildMonacoThemeData(themeRecord));

    monaco.editor.setTheme(MONACO_PLATFORM_THEME_NAME);

    return MONACO_PLATFORM_THEME_NAME;
};
